import { FC, useEffect, useState, useCallback } from 'react';
import { coreService, tournamentServer } from 'network-utils';
import { useSelector } from 'react-redux';
import { SmallLoader } from 'learning-panel-components';
import { selectIsTournamentsGamePackageEnabled, selectToken } from '@redux/slices/userSlice';
import { useGetChallengeUsersLeaderboard } from '@queries/core/challenges';
import { useGetTournamentUsersLeaderboard } from '@queries/tournaments/tournament';
import UserLeaderboard from './playUsersLeaderboard';
import { UserData } from '../leaderboard/userLeaderboard/userLeaderboard';
import styles from './playLeaderboardLoader.module.scss';
import { useGetCoreTournamentUsersLeaderboard } from '../../../queries/core/tournaments';

const UsersLeaderboardContainer: FC<{ isTournament: boolean; tournamentUuid?: string }> = ({
    isTournament,
    tournamentUuid
}: {
    isTournament: boolean;
    tournamentUuid?: string;
}) => {
    const token = useSelector(selectToken);
    const [allUsers, setAllUsers] = useState<UserData[]>([]);
    const [currentUser, setCurrentUser] = useState<UserData | null>(null);
    const [isFetchingData, setIsFetchingData] = useState<boolean>(true);
    const limit = 10;

    const isTournamentPackageEnabled = useSelector(selectIsTournamentsGamePackageEnabled);

    const { data: tournamentUsersLeaderboardData, isLoading: tournamentUsersLeaderboardLoading } =
        useGetTournamentUsersLeaderboard(
            token,
            isTournament && !isTournamentPackageEnabled,
            0,
            limit,
            undefined,
            tournamentUuid
        );

    const { data: tournamentUsersLeaderboardCoreData, isLoading: tournamentUsersLeaderboardCoreLoading } =
        useGetCoreTournamentUsersLeaderboard({
            token,
            enableQuery: isTournament && isTournamentPackageEnabled,
            offset: 0,
            limit,
            tournamentUuid
        });

    const { data: challengeUsersLeaderboardData, isLoading: challengeUsersLeaderboardLoading } =
        useGetChallengeUsersLeaderboard(token, isTournament);

    const mapUser = (user: coreService.UserLeaderboard) => ({
        rank: user.rank,
        nickname: user.nicknameOrEmail,
        score: Number(user.points),
        profilePicture: user.profilePicture,
        teams: user.teams
    });

    const mapUserTournamentLeaderboard = (user: tournamentServer.UserLeaderboard) => ({
        rank: user.rank,
        nickname: user.nickname,
        score: Number(user.score),
        avatar: user.avatar
    });

    useEffect(() => {
        if (!challengeUsersLeaderboardData?.usersLeaderboard) {
            setIsFetchingData(false);
            return;
        }
        setIsFetchingData(true);
        const { currentUser, topTen } = challengeUsersLeaderboardData?.usersLeaderboard;
        setCurrentUser(mapUser(currentUser));
        const users = topTen.map((user: coreService.UserLeaderboard) => mapUser(user));
        setAllUsers(users);
        setIsFetchingData(false);
    }, [challengeUsersLeaderboardData]);

    const handleLeaderboard = useCallback(
        (data: any) => {
            if (data && data.success === true) {
                const { currentUser, leaderboard } = data;
                setCurrentUser(mapUserTournamentLeaderboard(currentUser));
                const users = leaderboard
                    .slice(0, limit)
                    .map((user: tournamentServer.UserLeaderboard) => mapUserTournamentLeaderboard(user));
                setAllUsers(users);
            }
        },
        [setCurrentUser, setAllUsers]
    );

    useEffect(() => {
        handleLeaderboard(tournamentUsersLeaderboardData);
    }, [tournamentUsersLeaderboardData, handleLeaderboard]);

    useEffect(() => {
        handleLeaderboard(tournamentUsersLeaderboardCoreData);
    }, [tournamentUsersLeaderboardCoreData, handleLeaderboard]);

    return tournamentUsersLeaderboardLoading ||
        tournamentUsersLeaderboardCoreLoading ||
        challengeUsersLeaderboardLoading ||
        !currentUser ||
        isFetchingData ? (
        <div className={styles.loader}>
            <SmallLoader size="100px" />
        </div>
    ) : (
        currentUser && (
            <UserLeaderboard
                tournamentTable={isTournament}
                currentUser={currentUser}
                users={allUsers}
                onLoadMore={() => undefined}
                hasMore={false}
                tableHeight="23.5rem"
            />
        )
    );
};

export default UsersLeaderboardContainer;
